import * as THREE from 'three';
import {
  FURNISH_RATES,
  PALETTE,
  type Material,
  type PlacedButton,
  type PlacementSpace,
} from '@vrlearning/lesson-schema';
import { PX_PER_METRE } from '../../engine/text.js';
import { furnishLectern } from '../lectern.js';
import { Button } from '../panel.js';
import { MATERIAL_LOOKS, cssColour } from './materials.js';

/**
 * The furnish step's lectern controls (ADR-037; spec "The side lectern"): under the step's text,
 * the six room buttons (a fade teleport each), then ↺, the palette's eight swatches and ↻. The
 * palette is dimmed unless this pupil holds a piece; while the villa is locked the row says "De
 * villa is op slot. Kijk rond!" instead. Laid out by `furnishLectern()`, so the lectern's height is
 * measured by what is drawn. Children of the lectern's group, a hair proud of its panel.
 */

/** What pressing one of the controls asks for. */
export type FurnishAction =
  | { readonly room: string }
  | { readonly material: Exclude<Material, 'plain'> }
  | { readonly turn: number };

/** What the row says while the teacher has locked the villa. */
export const LOCKED_TEXT = 'De villa is op slot. Kijk rond!';
/** And in any other world. */
export const LOCKED_TEXT_SPACE = 'De ruimte is op slot. Kijk rond!';

/** How far in front of the panel the controls stand. */
const PROUD = 0.003;
/** The palette's opacity while nothing is held. */
const DIM = 0.3;

type Control = {
  readonly mesh: THREE.Mesh;
  readonly action: FurnishAction;
  readonly palette: boolean;
};

function canvasOf(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * PX_PER_METRE);
  canvas.height = Math.round(height * PX_PER_METRE);
  return canvas;
}

function textureOf(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** A swatch: a rounded square of the material's colour; the woods streaked, metal shaded. */
function drawSwatch(material: Exclude<Material, 'plain'>, place: PlacedButton): HTMLCanvasElement {
  const canvas = canvasOf(place.width, place.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const { width: w, height: h } = canvas;
  ctx.clearRect(0, 0, w, h);
  ctx.beginPath();
  ctx.roundRect(4, 4, w - 8, h - 8, 14);
  const look = MATERIAL_LOOKS[material];
  if (look.metalness > 0) {
    const shine = ctx.createLinearGradient(0, 0, w, h);
    shine.addColorStop(0, '#eef1f4');
    shine.addColorStop(0.5, cssColour(material));
    shine.addColorStop(1, '#6f747b');
    ctx.fillStyle = shine;
  } else {
    ctx.fillStyle = cssColour(material);
  }
  ctx.fill();
  if (look.grain) {
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.lineWidth = 3;
    for (let y = 14; y < h; y += 16) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(w * 0.3, y - 6, w * 0.6, y + 6, w, y - 2);
      ctx.stroke();
    }
    ctx.restore();
  }
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = 3;
  ctx.stroke();
  return canvas;
}

/** ↺ or ↻, drawn (not typed: a Quest font may lack the arrows) on a rounded square. */
function drawTurn(direction: 1 | -1, place: PlacedButton): HTMLCanvasElement {
  const canvas = canvasOf(place.width, place.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const { width: w, height: h } = canvas;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#3a4a6b';
  ctx.beginPath();
  ctx.roundRect(2, 2, w - 4, h - 4, 18);
  ctx.fill();
  const r = w * 0.27;
  const cx = w / 2;
  const cy = h / 2;
  ctx.strokeStyle = '#ffffff';
  ctx.fillStyle = '#ffffff';
  ctx.lineWidth = w * 0.08;
  ctx.lineCap = 'round';
  // Most of a circle, open at the top, its arrowhead at the end it turns towards.
  const from = -Math.PI / 2 + 0.5;
  const to = from + Math.PI * 1.35;
  ctx.beginPath();
  if (direction > 0) ctx.arc(cx, cy, r, to, from, true);
  else ctx.arc(cx, cy, r, from, to, false);
  ctx.stroke();
  const end = direction > 0 ? from : to;
  const tip = [cx + r * Math.cos(end), cy + r * Math.sin(end)] as const;
  const along = end + (direction > 0 ? -Math.PI / 2 : Math.PI / 2);
  const size = w * 0.2;
  ctx.beginPath();
  ctx.moveTo(tip[0] + Math.cos(along) * size, tip[1] + Math.sin(along) * size);
  ctx.lineTo(
    tip[0] + Math.cos(along + 2.4) * size * 0.8,
    tip[1] + Math.sin(along + 2.4) * size * 0.8,
  );
  ctx.lineTo(
    tip[0] + Math.cos(along - 2.4) * size * 0.8,
    tip[1] + Math.sin(along - 2.4) * size * 0.8,
  );
  ctx.closePath();
  ctx.fill();
  return canvas;
}

/** "De villa is op slot. Kijk rond!" in the palette's place. */
function drawLocked(place: PlacedButton, text: string): HTMLCanvasElement {
  const canvas = canvasOf(place.width, place.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const { width: w, height: h } = canvas;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#5a3d12';
  ctx.beginPath();
  ctx.roundRect(0, 0, w, h, h / 2);
  ctx.fill();
  ctx.fillStyle = '#ffe2b0';
  ctx.font = `bold ${Math.round(h * 0.5)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, w / 2, h / 2);
  return canvas;
}

function plane(place: PlacedButton, canvas: HTMLCanvasElement): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(place.width, place.height),
    new THREE.MeshBasicMaterial({
      map: textureOf(canvas),
      transparent: true,
      alphaTest: 0.02,
      depthTest: false,
    }),
  );
  mesh.position.set(place.x, place.y, PROUD);
  return mesh;
}

export class FurnishLectern {
  readonly group = new THREE.Group();
  readonly #controls: Control[] = [];
  readonly #rooms: Button[] = [];
  readonly #swatches = new Map<Exclude<Material, 'plain'>, THREE.Mesh>();
  readonly #ring: THREE.Mesh;
  readonly #locked: THREE.Mesh;
  #holding = false;
  #isLocked = false;
  readonly #raycaster = new THREE.Raycaster();

  constructor(parent: THREE.Object3D, space: PlacementSpace, renderOrder = 11) {
    const layout = furnishLectern();
    this.group.name = 'furnish-lectern';
    // The layout has six places; a space with fewer rooms leaves the later ones empty.
    space.rooms.slice(0, layout.rooms.length).forEach((room, i) => {
      const place = layout.rooms[i]!;
      const button = new Button(room.name, () => {}, {
        width: place.width,
        height: place.height,
        kind: 'secondary',
      });
      button.mesh.position.set(place.x, place.y, PROUD);
      button.mesh.material.depthTest = false;
      this.#rooms.push(button);
      this.#controls.push({ mesh: button.mesh, action: { room: room.id }, palette: false });
    });
    PALETTE.forEach((material, i) => {
      const place = layout.swatches[i]!;
      const mesh = plane(place, drawSwatch(material, place));
      this.#swatches.set(material, mesh);
      this.#controls.push({ mesh, action: { material }, palette: true });
    });
    const step = FURNISH_RATES.turnSnapDegrees;
    this.#controls.push({
      mesh: plane(layout.left, drawTurn(1, layout.left)),
      action: { turn: step },
      palette: true,
    });
    this.#controls.push({
      mesh: plane(layout.right, drawTurn(-1, layout.right)),
      action: { turn: -step },
      palette: true,
    });
    // The chosen swatch's ring: a white square a little larger, behind it.
    const first = layout.swatches[0]!;
    this.#ring = new THREE.Mesh(
      new THREE.PlaneGeometry(first.width + 0.022, first.height + 0.022),
      new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false }),
    );
    this.#ring.visible = false;
    this.#locked = plane(
      layout.locked,
      drawLocked(layout.locked, space.world === 'villa' ? LOCKED_TEXT : LOCKED_TEXT_SPACE),
    );
    this.#locked.visible = false;
    for (const mesh of [this.#ring, ...this.#controls.map((c) => c.mesh), this.#locked]) {
      mesh.renderOrder = renderOrder + (mesh === this.#ring ? 0 : 1);
      this.group.add(mesh);
    }
    this.group.visible = false;
    parent.add(this.group);
    this.#apply();
  }

  /** Shown during the furnish step only. */
  set shown(value: boolean) {
    this.group.visible = value;
  }

  get shown(): boolean {
    return this.group.visible;
  }

  /** The palette is lit while this pupil holds a piece, dimmed otherwise. */
  set holding(value: boolean) {
    if (value === this.#holding) return;
    this.#holding = value;
    this.#apply();
  }

  /** The teacher locked the villa: the row says so instead of the palette. */
  set locked(value: boolean) {
    if (value === this.#isLocked) return;
    this.#isLocked = value;
    this.#apply();
  }

  /** The held piece's material, ringed on the palette; none while nothing is held. */
  set material(material: Material | undefined) {
    const swatch = material && material !== 'plain' ? this.#swatches.get(material) : undefined;
    this.#ring.visible = swatch !== undefined && this.#holding && !this.#isLocked;
    if (swatch) this.#ring.position.set(swatch.position.x, swatch.position.y, PROUD / 2);
  }

  /** What may be pressed now: the rooms, and the palette while holding and not locked. */
  get pickables(): THREE.Object3D[] {
    if (!this.group.visible) return [];
    return this.#controls
      .filter((control) => !control.palette || this.#paletteLive())
      .map((control) => control.mesh);
  }

  /** The control a ray lands on, and how far along it. */
  along(ray: THREE.Ray): { action: FurnishAction; distance: number } | undefined {
    const pickables = this.pickables;
    if (pickables.length === 0) return undefined;
    this.#raycaster.set(ray.origin, ray.direction);
    const hit = this.#raycaster.intersectObjects(pickables, false)[0];
    if (!hit) return undefined;
    const control = this.#controls.find((c) => c.mesh === hit.object);
    return control ? { action: control.action, distance: hit.distance } : undefined;
  }

  /** The room buttons lit where a ray rests on them. */
  hover(rays: readonly THREE.Ray[]): void {
    const hovered = new Set<THREE.Object3D>();
    for (const ray of rays) {
      this.#raycaster.set(ray.origin, ray.direction);
      const hit = this.#raycaster.intersectObjects(this.pickables, false)[0];
      if (hit) hovered.add(hit.object);
    }
    for (const button of this.#rooms) button.hovered = hovered.has(button.mesh);
  }

  #paletteLive(): boolean {
    return this.#holding && !this.#isLocked;
  }

  #apply(): void {
    for (const control of this.#controls) {
      if (!control.palette) continue;
      control.mesh.visible = !this.#isLocked;
      const material = control.mesh.material as THREE.MeshBasicMaterial;
      material.opacity = this.#holding ? 1 : DIM;
    }
    this.#locked.visible = this.#isLocked;
    if (!this.#paletteLive()) this.#ring.visible = false;
  }

  dispose(): void {
    for (const button of this.#rooms) button.dispose();
    for (const control of this.#controls) {
      if (this.#rooms.some((button) => button.mesh === control.mesh)) continue;
      const material = control.mesh.material as THREE.MeshBasicMaterial;
      material.map?.dispose();
      material.dispose();
      control.mesh.geometry.dispose();
    }
    const lockedMaterial = this.#locked.material as THREE.MeshBasicMaterial;
    lockedMaterial.map?.dispose();
    lockedMaterial.dispose();
    this.#locked.geometry.dispose();
    (this.#ring.material as THREE.Material).dispose();
    this.#ring.geometry.dispose();
    this.group.removeFromParent();
  }
}
